import { Button, Chip } from "@mui/material";
import { History } from "@mui/icons-material";
import { PrescriptionsActions } from "../../Prescriptions/PrescriptionsActions";
import { isMobile } from "react-device-detect";
import { NoDataWarning } from "@/components/common";
import {
  PrescriptionMobileList,
  type PrescriptionStackItem,
} from "@/components/common/prescriptionStack/PrescriptionStackCarousel";
import { PatientPrescriptionCardActions } from "@/components/common/prescriptionStack/PatientPrescriptionCardActions";
import { sanitizeSpecialty } from "@/components/common/prescriptionStack/prescriptionStackTheme";
import { isDocumentSigned } from "@/utils/fhirDocumentUtils";
import { DocumentCard } from "../../../../../../components/common/DocumentCard";
import { useEhrRecordsMobileLayout } from "../../../../context/EhrRecordsLayoutContext";
import { EhrResponsiveTable } from "../../../mobile/EhrResponsiveTable";
import { useDocumentHistoryColumn } from "./useDocumentHistoryColumn";

export function MedicalNotes({
  rowData = [],
  handleOpen,
  setBinaryUrl,
  sign,
  setRefetch,
  medicationRequest,
}: any) {
  const isEhrMobile = useEhrRecordsMobileLayout();
  const { displayRows, historyDialog, openHistory, rowHasHistory } =
    useDocumentHistoryColumn({
      rowData,
      handleOpen,
      setBinaryUrl,
    });

  const columns = [
    { column: "Medicamentos", rowName: "description" },
    { column: "Profissional", rowName: "practitioner" },
    { column: "Especialidade", rowName: "specialty" },
    { column: "Emitido em", rowName: "firstDate" },
    { column: "Última remissão", rowName: "lastDateCell" },
    { column: "Histórico", rowName: "historyAction", width: "12%" },
  ];

  // Dashboard mobile (ViewDocs): carousel dedicado de receitas.
  // No sumário EHR (registros), mantém cards + ações via EhrResponsiveTable.
  if (isMobile && !isEhrMobile) {
    if (!rowData?.length) {
      return <NoDataWarning message="Nenhuma receita médica encontrada." />;
    }

    const items: PrescriptionStackItem[] = rowData.map((row: any) => ({
      id: row.id,
      description: row.description,
      date: row.firstDate || row.date,
      practitioner: row.practitioner,
      specialty: sanitizeSpecialty(row.specialty) ?? undefined,
      sign: isDocumentSigned(row.fullResource),
      binaryUrl: row.binaryUrl,
      fullResource: row.fullResource,
      organization: row.organization,
      posologyLines: row.posologyLines,
    }));

    return (
      <>
        <PrescriptionMobileList
          items={items}
          medicationRequest={medicationRequest}
          handleOpen={handleOpen}
          setBinaryUrl={setBinaryUrl}
          renderActions={(row) => (
            <PatientPrescriptionCardActions
              row={rowData.find((r: any) => r.id === row.id) ?? row}
              handleOpen={handleOpen}
              setBinaryUrl={setBinaryUrl}
            />
          )}
        />
        {historyDialog}
      </>
    );
  }

  return (
    <>
      <EhrResponsiveTable
        title="Receitas médicas"
        hideSectionHeader
        columns={columns}
        rows={displayRows}
        actions={(row: any) => (
          <PrescriptionsActions
            setRefetch={setRefetch}
            row={row}
            handleOpen={handleOpen}
            setBinaryUrl={setBinaryUrl}
          />
        )}
        renderCard={(row: any) => (
          <DocumentCard
            key={row.id ?? row.description}
            title={row.description || "Prescrição Médica"}
            date={row.firstDate || row.date}
            dateLabel="Emitido em"
            lastDate={rowHasHistory(row) ? row.lastDate : undefined}
            singleIssue={!rowHasHistory(row)}
            practitioner={row.practitioner}
            specialty={row.specialty}
            actions={
              <PrescriptionsActions
                setRefetch={setRefetch}
                row={row}
                handleOpen={handleOpen}
                setBinaryUrl={setBinaryUrl}
              />
            }
          >
            {/* No mobile, equivalente à coluna Histórico */}
            {rowHasHistory(row) ? (
              <Button
                variant="text"
                color="primary"
                startIcon={<History />}
                onClick={() => openHistory(row)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Histórico
              </Button>
            ) : (
              <Chip
                label="Emissão única"
                variant="outlined"
                sx={{
                  fontWeight: 600,
                  color: "text.secondary",
                  borderColor: "divider",
                }}
              />
            )}
          </DocumentCard>
        )}
      />
      {historyDialog}
    </>
  );
}
